import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { StarRating } from '@/components/ui/star-rating';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { useApp } from '@/components/AppLayout';
import { clientReviewApi, queryFn } from '@/lib/api';
import { ArrowLeft, Star, User, FileText, Calendar } from 'lucide-react';
import { format } from 'date-fns';

export function ViewReview() {
  const { id } = useParams<{ id: string }>();
  const { nav } = useBusinessNav();
  const { currentBusiness } = useApp();

  const { data: reviews = [] } = useQuery({
    queryKey: ['reviews', currentBusiness?.id],
    queryFn: () => queryFn(clientReviewApi.list(currentBusiness!.id)),
    enabled: !!currentBusiness,
  });

  const review = reviews.find((r) => r.id === id);

  if (!review) return <div className="fixed inset-0 z-50 bg-background" />;

  return (
    <div className="px-4 sm:px-8 pt-4 pb-8">
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => nav('/reviews')}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          All Reviews
        </button>
      </div>

      <Card>
        <CardContent className="p-6 space-y-6">
          {/* Review Header */}
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Star className="h-7 w-7 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-semibold truncate">{review.client_name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <StarRating value={review.rating} readonly size="sm" />
                <span className="text-sm text-muted-foreground">{review.rating}/5</span>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-2">
            <Label className="text-muted-foreground">Details</Label>
            <div className="border rounded-lg divide-y">
              <div
                className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => nav(`/clients/${review.client}`)}
              >
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{review.client_name}</span>
              </div>
              {review.invoice && (
                <div
                  className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => nav(`/invoices/${review.invoice}`)}
                >
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Invoice #{review.invoice_number}</span>
                </div>
              )}
              <div className="flex items-center gap-3 px-4 py-3">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{format(new Date(review.created_at), 'MMMM d, yyyy')}</span>
              </div>
            </div>
          </div>

          {/* Comments */}
          {review.comments && (
            <div className="space-y-2">
              <Label className="text-muted-foreground">Comments</Label>
              <div className="border rounded-lg px-4 py-3 text-sm whitespace-pre-wrap">
                {review.comments}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
